import React, { useState, useMemo } from "react";
import { RAW, TX } from "../data/heatmapData";

const CAT_COLORS = {
  Phylum:    { bg: "rgba(139,92,246,.22)",  text: "#c4b5fd" },
  Class:     { bg: "rgba(59,130,246,.22)",  text: "#93c5fd" },
  Order:     { bg: "rgba(20,184,166,.22)",  text: "#5eead4" },
  Family:    { bg: "rgba(34,197,94,.22)",   text: "#86efac" },
  Genus:     { bg: "rgba(234,179,8,.22)",   text: "#fde047" },
  Species:   { bg: "rgba(249,115,22,.22)",  text: "#fdba74" },
  Metabolite:{ bg: "rgba(16,185,129,.22)",  text: "#6ee7b7" },
  Pathway:   { bg: "rgba(236,72,153,.22)",  text: "#f9a8d4" },
  Other:     { bg: "rgba(148,163,184,.22)", text: "#cbd5e1" },
};

const HOST_COLORS = {
  "Human":                   { bg: "rgba(16,185,129,.28)",  text: "#6ee7b7" },
  "Mouse":                   { bg: "rgba(234,179,8,.28)",   text: "#fde047" },
  "Rat":                     { bg: "rgba(249,115,22,.28)",  text: "#fdba74" },
  "Dog":                     { bg: "rgba(0,121,192,.28)",   text: "#60b4ff" },
  "Drosophila melanogaster": { bg: "rgba(139,92,246,.28)",  text: "#c4b5fd" },
};

const HOST_SHORT = {
  "Human": "Human", "Mouse": "Mouse", "Rat": "Rat",
  "Dog": "Dog", "Drosophila melanogaster": "Drosoph.",
};

const ALL_TAXA    = [...new Set(RAW.flatMap(r => [...r.increased, ...r.decreased]))]
  .filter(t => t && !/^\d+$/.test(t.trim()))
  .sort();
const UNIQUE_HOSTS = [...new Set(RAW.map(r => r.host))].sort();
const UNIQUE_EPI   = [...new Set(RAW.map(r => r.epilepsy))].sort();
const CATS = ["Phylum","Class","Order","Family","Genus","Species","Metabolite","Pathway","Other"];

const DEFAULT_COL_W = 52;
const MIN_COL_W     = 14;
const MAX_COL_W     = 90;
const ZOOM_STEP     = 8;

export default function Heatmap() {
  const [search, setSearch] = useState("");
  const [catFilter, setCat] = useState("");
  const [hostFilter, setHost] = useState("");
  const [epiFilter, setEpi]   = useState("");
  const [dirFilter, setDir]   = useState("");
  const [colW, setColW] = useState(DEFAULT_COL_W);

  const zoomIn  = () => setColW(w => Math.min(w + ZOOM_STEP, MAX_COL_W));
  const zoomOut = () => setColW(w => Math.max(w - ZOOM_STEP, MIN_COL_W));

  const filteredCols = useMemo(() =>
    RAW.filter(r =>
      (!hostFilter || r.host === hostFilter) &&
      (!epiFilter  || r.epilepsy === epiFilter)
    ), [hostFilter, epiFilter]);

  const filteredRows = useMemo(() =>
    ALL_TAXA.filter(taxa => {
      if (search && !taxa.toLowerCase().includes(search.toLowerCase())) return false;
      if (catFilter && TX[taxa] !== catFilter) return false;
      const appears = filteredCols.some(r => r.increased.includes(taxa) || r.decreased.includes(taxa));
      if (!appears) return false;
      if (dirFilter === "up"   && !filteredCols.some(r => r.increased.includes(taxa))) return false;
      if (dirFilter === "down" && !filteredCols.some(r => r.decreased.includes(taxa))) return false;
      return true;
    }), [search, catFilter, dirFilter, filteredCols]);

  /* Group consecutive same-study columns */
  const studyGroups = useMemo(() => {
    const groups = [];
    filteredCols.forEach(col => {
      if (!groups.length || groups[groups.length - 1].study !== col.study)
        groups.push({ study: col.study, count: 1 });
      else
        groups[groups.length - 1].count++;
    });
    return groups;
  }, [filteredCols]);

  /* Group consecutive same-host columns */
  const hostGroups = useMemo(() => {
    const groups = [];
    filteredCols.forEach(col => {
      if (!groups.length || groups[groups.length - 1].host !== col.host)
        groups.push({ host: col.host, count: 1 });
      else
        groups[groups.length - 1].count++;
    });
    return groups;
  }, [filteredCols]);

  const reset = () => { setSearch(""); setCat(""); setHost(""); setEpi(""); setDir(""); };

  const cellInfo = (taxa, col) => {
    const inc = col.increased.includes(taxa);
    const dec = col.decreased.includes(taxa);
    if (inc && dec) return { bg: "rgba(167,139,250,.28)", color: "#a78bfa", symbol: "↕" };
    if (inc)        return { bg: "rgba(239,68,68,.22)",   color: "#ef4444", symbol: "↑" };
    if (dec)        return { bg: "rgba(59,130,246,.22)",  color: "#3b82f6", symbol: "↓" };
    return { bg: "transparent", color: "transparent", symbol: "" };
  };

  const TAXON_COL = 240;

  /* Zoom button helper */
  const zoomBtn = (onClick, disabled, label) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label === "−" ? "Zoom out" : "Zoom in"}
      style={{
        width: 30, height: 30, borderRadius: "6px",
        border: "1px solid #2d5078",
        background: disabled ? "#0f2133" : "#1a3550",
        color: disabled ? "#3d5a78" : "#c8dff0",
        fontSize: "1.15rem", fontWeight: "700", lineHeight: 1,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.15s",
      }}
    >{label}</button>
  );

  /* Header label cell (sticky-left column in thead) */
  const labelCell = {
    position: "sticky", left: 0, zIndex: 30,
    background: "#0d1f35",
    padding: "0 0.75rem",
    border: "1px solid #1e3a5f",
    borderRight: "2px solid #2d5078",
    fontWeight: "700",
    fontSize: "0.72rem",
    color: "#6b9ab8",
    textAlign: "left",
    whiteSpace: "nowrap",
    width: TAXON_COL,
    minWidth: TAXON_COL,
  };

  const selectStyle = {
    padding: "0.5rem 0.75rem",
    borderRadius: "8px",
    border: "1px solid #2d5078",
    fontSize: "0.85rem",
    background: "#1a3550",
    color: "#c8dff0",
    outline: "none",
    cursor: "pointer",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0d1b2e",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>

      {/* ── Header / Filters ── */}
      <div style={{
        background: "#0a2540",
        borderBottom: "1px solid #1e3a5f",
        padding: "1.25rem 2rem",
        boxShadow: "0 2px 12px rgba(0,0,0,0.35)",
      }}>
        {/* Title row */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <h1 style={{ color: "#60b4ff", fontSize: "1.6rem", fontWeight: "700", margin: 0 }}>
            Microbiome Heatmap
          </h1>
          <span style={{ color: "#4a7a9b", fontSize: "0.85rem" }}>
            {filteredRows.length} taxa &times; {filteredCols.length} comparisons
          </span>
          {/* Zoom controls */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.4rem" }}>
            {zoomBtn(zoomOut, colW <= MIN_COL_W, "−")}
            <span style={{ fontSize: "0.78rem", color: "#6b9ab8", minWidth: "2.5rem", textAlign: "center" }}>
              {Math.round((colW / DEFAULT_COL_W) * 100)}%
            </span>
            {zoomBtn(zoomIn, colW >= MAX_COL_W, "+")}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", alignItems: "center" }}>
          <input
            style={{ ...selectStyle, minWidth: "170px" }}
            placeholder="Search taxa…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={selectStyle} value={catFilter} onChange={e => setCat(e.target.value)}>
            <option value="">All Categories</option>
            {CATS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={selectStyle} value={hostFilter} onChange={e => setHost(e.target.value)}>
            <option value="">All Hosts</option>
            {UNIQUE_HOSTS.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <select style={{ ...selectStyle, minWidth: "220px" }} value={epiFilter} onChange={e => setEpi(e.target.value)}>
            <option value="">All Epilepsy Types</option>
            {UNIQUE_EPI.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select style={selectStyle} value={dirFilter} onChange={e => setDir(e.target.value)}>
            <option value="">Both ↑↓</option>
            <option value="up">Increased ↑</option>
            <option value="down">Decreased ↓</option>
          </select>
          {(search || catFilter || hostFilter || epiFilter || dirFilter) && (
            <button onClick={reset} style={{
              background: "#0079C0", color: "white", border: "none",
              borderRadius: "8px", padding: "0.5rem 1rem",
              cursor: "pointer", fontWeight: "600", fontSize: "0.85rem",
            }}>
              Clear Filters
            </button>
          )}
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: "1.5rem", marginTop: "0.85rem", flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Increased", bg: "rgba(239,68,68,.22)",   color: "#ef4444", sym: "↑" },
            { label: "Decreased", bg: "rgba(59,130,246,.22)",  color: "#3b82f6", sym: "↓" },
            { label: "Both",      bg: "rgba(167,139,250,.28)", color: "#a78bfa", sym: "↕" },
          ].map(l => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "#6b9ab8" }}>
              <span style={{
                background: l.bg, color: l.color,
                padding: "2px 10px", borderRadius: "5px",
                fontWeight: "800", fontSize: "1rem", lineHeight: 1.4,
              }}>{l.sym}</span>
              {l.label}
            </div>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            {Object.entries(HOST_COLORS).map(([host, c]) => (
              <span key={host} style={{ background: c.bg, color: c.text, padding: "2px 8px", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600" }}>
                {HOST_SHORT[host]}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{
        overflowX: "auto",
        margin: "1.25rem",
        borderRadius: "10px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.09)",
        border: "1px solid #e2e8f0",
        background: "#0a1929",
      }}>
        {filteredCols.length === 0 || filteredRows.length === 0 ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "#6b9ab8" }}>
            <p style={{ fontSize: "1.05rem", fontWeight: 600, marginBottom: "0.75rem" }}>No data matches your filters</p>
            <button onClick={reset} style={{ background: "#0079C0", color: "#fff", border: "none", borderRadius: 6, padding: "0.5rem 1.25rem", cursor: "pointer", fontWeight: 600 }}>
              Clear Filters
            </button>
          </div>
        ) : (
          <table style={{ borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 20 }}>

              {/* Row 1 — Study names */}
              <tr>
                <th style={{
                  ...labelCell, zIndex: 31,
                  background: "#0a2540", color: "#c8dff0",
                  fontSize: "0.8rem", textAlign: "center",
                  height: "36px",
                }}>
                  Taxon / Metabolite
                </th>
                {studyGroups.map((g, i) => (
                  <th key={i} colSpan={g.count} style={{
                    background: i % 2 === 0 ? "#0a2540" : "#0f3460",
                    color: "#e2eaf4",
                    border: "1px solid #1e3a5f",
                    fontWeight: "700",
                    fontSize: "0.78rem",
                    textAlign: "center",
                    padding: "0.4rem 0.25rem",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: `${g.count * colW}px`,
                    height: "36px",
                  }}>
                    {g.study}
                  </th>
                ))}
              </tr>

              {/* Row 2 — Host (merged consecutive same-host spans) */}
              <tr>
                <th style={labelCell}>Host</th>
                {hostGroups.map((g, i) => {
                  const hc = HOST_COLORS[g.host] || { bg: "#f1f5f9", text: "#374151" };
                  return (
                    <th key={i} colSpan={g.count} style={{
                      background: hc.bg,
                      color: hc.text,
                      border: "1px solid #e2e8f0",
                      fontSize: "0.72rem",
                      fontWeight: "700",
                      textAlign: "center",
                      padding: "0.3rem 0.1rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      maxWidth: `${g.count * colW}px`,
                    }}>
                      {HOST_SHORT[g.host] || g.host}
                    </th>
                  );
                })}
              </tr>

              {/* Row 3 — Epilepsy type (rotated) */}
              <tr>
                <th style={labelCell}>Epilepsy Type</th>
                {filteredCols.map((col, i) => (
                  <th key={i} title={col.epilepsy} style={{
                    background: "#0f2133",
                    border: "1px solid #1e3a5f",
                    width: colW, minWidth: colW, maxWidth: colW,
                    height: "160px",
                    padding: "4px 0",
                    verticalAlign: "bottom",
                    textAlign: "center",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      fontSize: "0.7rem",
                      color: "#c8dff0",
                      fontWeight: "500",
                      lineHeight: 1.3,
                      maxHeight: "152px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      display: "block",
                      paddingBottom: "4px",
                    }}>
                      {col.epilepsy}
                    </div>
                  </th>
                ))}
              </tr>

              {/* Row 4 — Comparison (rotated) */}
              <tr>
                <th style={labelCell}>Comparison</th>
                {filteredCols.map((col, i) => (
                  <th key={i} title={col.comparison} style={{
                    background: "#0d1b2e",
                    border: "1px solid #1e3a5f",
                    borderBottom: "2px solid #2d5078",
                    width: colW, minWidth: colW, maxWidth: colW,
                    height: "220px",
                    padding: "4px 0",
                    verticalAlign: "bottom",
                    textAlign: "center",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      fontSize: "0.7rem",
                      color: "#c8dff0",
                      fontWeight: "500",
                      lineHeight: 1.3,
                      maxHeight: "212px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      display: "block",
                      paddingBottom: "4px",
                    }}>
                      {col.comparison}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((taxa, ri) => {
                const cat = TX[taxa] || "Other";
                const cc  = CAT_COLORS[cat] || CAT_COLORS.Other;
                const rowBg = ri % 2 === 0 ? "#0f1e2e" : "#0c1825";
                return (
                  <tr key={ri}>
                    <td style={{
                      position: "sticky", left: 0, zIndex: 10,
                      background: rowBg,
                      padding: "0.35rem 0.75rem",
                      borderRight: "2px solid #2d5078",
                      borderBottom: "1px solid #1e3a5f",
                      width: TAXON_COL, minWidth: TAXON_COL, maxWidth: TAXON_COL,
                      fontSize: "0.82rem",
                      color: "#c8dff0",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <span style={{
                          background: cc.bg, color: cc.text,
                          padding: "1px 5px", borderRadius: "4px",
                          fontSize: "0.62rem", fontWeight: "700",
                          whiteSpace: "nowrap", flexShrink: 0,
                        }}>
                          {cat}
                        </span>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={taxa}>
                          {taxa}
                        </span>
                      </div>
                    </td>
                    {filteredCols.map((col, ci) => {
                      const c = cellInfo(taxa, col);
                      return (
                        <td
                          key={ci}
                          title={c.symbol
                            ? `${taxa}\n${c.symbol === "↑" ? "Increased" : c.symbol === "↓" ? "Decreased" : "Both"}\n${col.study}: ${col.comparison}`
                            : ""}
                          style={{
                            background: c.bg,
                            color: c.color,
                            textAlign: "center",
                            fontWeight: "800",
                            fontSize: "1rem",
                            border: "1px solid #1a2f47",
                            width: colW, maxWidth: colW,
                            padding: "0.25rem 0",
                            userSelect: "none",
                            transition: "filter 0.1s",
                            lineHeight: 1.4,
                          }}
                          onMouseEnter={e => { if (c.symbol) e.currentTarget.style.filter = "brightness(0.82)"; }}
                          onMouseLeave={e => { e.currentTarget.style.filter = "none"; }}
                        >
                          {c.symbol}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
